import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@common/store/hooks";
import {
  setSelectedWorkspace,
  setApiUrl,
  clearError,
  loadWorkspaces,
  loadAllWorkspaces,
  loadAllNotes,
  loadAllLinks,
} from "@common/store/slices/workspaceSlice";
import {
  loadGroupsWithDetails,
  setGroups,
} from "@common/store/slices/groupSlice";
import type { WorkspaceSummary } from "@common/store/slices/workspaceSlice";
import type { NormalizedGroup } from "@common/types/entities";

export function useWorkspace() {
  const dispatch = useAppDispatch();
  const workspaceState = useAppSelector((state) => state.workspace);
  const linksFromLinkSlice = useAppSelector((state) => state.links.links);

  const handleSetSelectedWorkspace = useCallback(
    (workspace: WorkspaceSummary | null) => {
      dispatch(setSelectedWorkspace(workspace));
    },
    [dispatch],
  );

  const handleSetApiUrl = useCallback(
    (url: string | null) => {
      dispatch(setApiUrl(url));
    },
    [dispatch],
  );

  const handleLoadWorkspaces = useCallback(() => {
    return dispatch(loadWorkspaces());
  }, [dispatch]);

  const handleLoadAllWorkspaces = useCallback(
    (options?: { forceRefresh?: boolean }) => {
      return dispatch(loadAllWorkspaces(options));
    },
    [dispatch],
  );

  const handleLoadAllNotes = useCallback(() => {
    return dispatch(loadAllNotes());
  }, [dispatch]);

  const handleLoadAllLinks = useCallback(() => {
    return dispatch(loadAllLinks());
  }, [dispatch]);

  const handleLoadWorkspaceGroups = useCallback(() => {
    return dispatch(loadGroupsWithDetails(undefined));
  }, [dispatch]);

  const handleLoadAllGroups = useCallback(
    (options?: { forceRefresh?: boolean }) => {
      return dispatch(loadGroupsWithDetails(options));
    },
    [dispatch],
  );

  const handleSetGroups = useCallback(
    (groups: NormalizedGroup[]) => {
      dispatch(setGroups(groups));
    },
    [dispatch],
  );

  const handleRefreshWorkspaces = useCallback(() => {
    return dispatch(loadAllWorkspaces({ forceRefresh: true }));
  }, [dispatch]);

  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    // Only return specific fields to avoid creating new object references on every state change
    workspaces: workspaceState.workspaces,
    allWorkspaces: workspaceState.allWorkspaces,
    allNotes: workspaceState.allNotes,
    links: linksFromLinkSlice, // Read from linkSlice instead of workspaceState
    groups: workspaceState.groups,
    selectedWorkspace: workspaceState.selectedWorkspace,
    apiUrl: workspaceState.apiUrl,
    loading: workspaceState.loading,
    error: workspaceState.error,
    setSelectedWorkspace: handleSetSelectedWorkspace,
    setApiUrl: handleSetApiUrl,
    clearError: handleClearError,
    loadWorkspaces: handleLoadWorkspaces,
    loadAllWorkspaces: handleLoadAllWorkspaces,
    loadAllNotes: handleLoadAllNotes,
    loadAllLinks: handleLoadAllLinks,
    loadWorkspaceGroups: handleLoadWorkspaceGroups,
    loadAllGroups: handleLoadAllGroups,
    setGroups: handleSetGroups,
    refreshWorkspaces: handleRefreshWorkspaces,
  };
}
